import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { fetchWithAuth } from "../../../utils/api";
import { swalConfirm, swalError, swalSuccess, swalWarning } from "../../../utils/swalAlert";
import { formatPhoneNumber } from "../../../utils/formatPhoneNumber";
import Modal from "../../ui/Modal";
import Loader from "../../ui/Loader";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

export default function InstructorLevelPlacementZone({ institute: propInstitute }) {
  const { user } = useContext(AuthContext);
  const [institute, setInstitute] = useState(propInstitute || user?.institute || null);

  // Fetch institute details if not provided via props/auth
  useEffect(() => {
    if (propInstitute) {
      setInstitute(propInstitute);
      return;
    }
    const fetchInstituteData = async () => {
      const instituteId = user?.instituteId || user?.institute?._id;
      if (!instituteId) return;
      try {
        const res = await fetchWithAuth(`${serverUrl}/api/institute/get/${instituteId}`, {
          credentials: "include"
        });
        if (res && res.ok) {
          const data = await res.json();
          setInstitute(data);
        }
      } catch (err) {
        console.error("Failed to fetch institute info in LevelPlacementZone:", err);
      }
    };
    fetchInstituteData();
  }, [propInstitute, user?.instituteId, user?.institute?._id]);

  // Dynamic Level Options from institute?.levels array
  const levelOptions = useMemo(() => {
    const rawLevels = institute?.levels;
    if (Array.isArray(rawLevels) && rawLevels.length > 0) {
      const dynamicLevels = rawLevels.map((lvl) => {
        if (typeof lvl === "object" && lvl !== null) {
          return {
            value: lvl.value || lvl.name || lvl.label || String(lvl),
            label: lvl.label || lvl.name || lvl.value || String(lvl)
          };
        }
        return {
          value: String(lvl),
          label: String(lvl)
        };
      });
      return [{ value: "All", label: "All Levels" }, ...dynamicLevels];
    }
    return [{ value: "All", label: "All Levels" }];
  }, [institute?.levels]);

  // Core candidate data
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLevelFilter, setSelectedLevelFilter] = useState("All");
  const [selectedStageFilter, setSelectedStageFilter] = useState("all");

  // Placement class reference
  const [placementClassId, setPlacementClassId] = useState(null);

  // Kebab Dropdown Menu State
  const [openKebabMenuId, setOpenKebabMenuId] = useState(null);
  const kebabRef = useRef(null);

  // Modal 1: Test Creation Modal (AI Exam Generator)
  const [isCreateTestModalOpen, setIsCreateTestModalOpen] = useState(false);
  const [activeCandidateForTest, setActiveCandidateForTest] = useState(null);
  const [weightSplitOption, setWeightSplitOption] = useState("70-30"); // '70-30' | '80-20'
  const [examDuration, setExamDuration] = useState(70);
  const [customPrompt, setCustomPrompt] = useState("");
  const [isGeneratingTest, setIsGeneratingTest] = useState(false);
  const [selectedTargetLevel, setSelectedTargetLevel] = useState("");

  // Modal 2: Answer Paper Upload & AssessQ AI Grading Modal (Supports Online & Batch Modes)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [activeCandidateForUpload, setActiveCandidateForUpload] = useState(null);
  const [answerPaperFile, setAnswerPaperFile] = useState(null);
  const [isUploadingAndGrading, setIsUploadingAndGrading] = useState(false);
  const [uploadGradingResult, setUploadGradingResult] = useState(null);

  // Modal 3: Oral Inquiry / Viva Voce Modal
  const [isOralModalOpen, setIsOralModalOpen] = useState(false);
  const [activeCandidateForOral, setActiveCandidateForOral] = useState(null);
  const [oralScore, setOralScore] = useState(0);
  const [oralNotes, setOralNotes] = useState("");
  const [oralRecommendation, setOralRecommendation] = useState("");
  const [isSavingOral, setIsSavingOral] = useState(false);

  // Modal 4: Diagnostic Details Modal
  const [isDiagnosticModalOpen, setIsDiagnosticModalOpen] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);


  // Close kebab menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target)) {
        setOpenKebabMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch candidates from API
  const fetchCandidates = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/level-test/students`);
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch level test candidates");
      }
      const resData = await response.json();
      setCandidates(resData.data || []);
      if (resData.placementClassId) {
        setPlacementClassId(resData.placementClassId);
      }
    } catch (err) {
      console.error("Error loading placement candidates:", err);
      swalError(err.message || "Failed to load candidates");
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, []);

  // Fetch placement class reference ID
  const fetchPlacementClass = useCallback(async () => {
    const instructorId = user?._id || user?.id;
    if (!instructorId) return;
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/class/get-all/${instructorId}`);
      if (response && response.ok) {
        const classes = await response.json();
        if (Array.isArray(classes)) {
          const pClass = classes.find((c) => c.isLevelPlacementZone);
          if (pClass) {
            setPlacementClassId(pClass._id);
          }
        }
      }
    } catch (err) {
      console.error("Error finding placement class:", err);
    }
  }, [user?._id, user?.id]);

  useEffect(() => {
    fetchCandidates();
    fetchPlacementClass();
  }, [fetchCandidates, fetchPlacementClass]);

  // Stage evaluation helper
  const getCandidateStage = useCallback((candidate) => {
    const hasExam = !!candidate.placementTest;
    const pt = candidate.placementTest;
    const paperStatus = pt?.paperStatus;
    const writtenScore = pt?.writtenScore;
    const oralStatus = pt?.oralStatus;
    const placementStatus = pt?.placementStatus;

    if (placementStatus === "Approved" || placementStatus === "Rejected") return "completed";
    if (!hasExam) return "need_exam";
    if (pt?.status === "Generating") return "generating";
    if (pt?.status === "Failed") return "failed";
    if (paperStatus === "Requested") return "batch_requested";
    if (writtenScore == null) return "grading_pending";
    if (oralStatus !== "Completed") return "viva_pending";
    return "ready_decision";
  }, []);

  // Key Metrics & Pipeline Counts
  const stats = useMemo(() => {
    let needExam = 0;
    let gradingPending = 0;
    let batchRequested = 0;
    let vivaPending = 0;
    let readyDecision = 0;
    let completed = 0;

    candidates.forEach((c) => {
      const stage = getCandidateStage(c);
      if (stage === "need_exam") needExam++;
      else if (stage === "grading_pending") gradingPending++;
      else if (stage === "batch_requested") batchRequested++;
      else if (stage === "viva_pending") vivaPending++;
      else if (stage === "ready_decision") readyDecision++;
      else if (stage === "completed") completed++;
    });

    return {
      total: candidates.length,
      needExam,
      gradingPending,
      batchRequested,
      vivaPending,
      readyDecision,
      completed
    };
  }, [candidates, getCandidateStage]);

  // Filtered Candidates based on Search, Level Dropdown, and Stage Tab
  const filteredCandidates = useMemo(() => {
    return candidates.filter((c) => {
      const fullName = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
      const email = (c.email || "").toLowerCase();
      const refId = (c.referenceId || "").toLowerCase();
      const search = searchTerm.toLowerCase().trim();

      const matchesSearch =
        !search || fullName.includes(search) || email.includes(search) || refId.includes(search);

      const studentLevel = c.level || c.placementTest?.targetLevel || "";
      const matchesLevel = selectedLevelFilter === "All" || studentLevel === selectedLevelFilter;

      const stage = getCandidateStage(c);
      const matchesStage = selectedStageFilter === "all" || stage === selectedStageFilter;

      return matchesSearch && matchesLevel && matchesStage;
    });
  }, [candidates, searchTerm, selectedLevelFilter, selectedStageFilter, getCandidateStage]);


  // Refresh Results Handler (like SubmittedAssessments.jsx for batch grading completion)
  const handleRefreshResults = async () => {
    setRefreshing(true);
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/level-test/students`);
      if (!response || !response.ok) {
        throw new Error("Failed to refresh candidate records");
      }
      const resData = await response.json();
      const newCandidates = resData.data || [];

      // Check if any candidates that were previously "Requested" are now graded
      const previouslyRequestedIds = new Set(
        candidates
          .filter((c) => c.placementTest?.paperStatus === "Requested")
          .map((c) => c._id)
      );

      let newlyGradedCount = 0;
      let stillRequestedCount = 0;

      newCandidates.forEach((nc) => {
        if (previouslyRequestedIds.has(nc._id)) {
          if (nc.placementTest?.writtenScore != null || nc.placementTest?.paperStatus === "Graded") {
            newlyGradedCount++;
          } else if (nc.placementTest?.paperStatus === "Requested") {
            stillRequestedCount++;
          }
        }
      });

      setCandidates(newCandidates);
      if (resData.placementClassId) setPlacementClassId(resData.placementClassId);

      if (newlyGradedCount > 0) {
        swalSuccess(`Results refreshed! AI Grading completed for ${newlyGradedCount} candidate(s).`);
      } else if (stillRequestedCount > 0) {
        swalWarning(`AI Grading is still in progress for ${stillRequestedCount} candidate(s). Please check back later.`);
      } else {
        swalSuccess("Placement candidates and grading statuses refreshed successfully!");
      }
    } catch (err) {
      console.error("Refresh results error:", err);
      swalError(err.message || "Failed to refresh results");
    } finally {
      setRefreshing(false);
    }
  };



  // Open Test Creation Modal
  const handleOpenCreateTest = (candidate) => {
    setActiveCandidateForTest(candidate);
    const fallbackLevel = levelOptions.find((l) => l.value !== "All")?.value || "A1";
    setSelectedTargetLevel(
      candidate.level || candidate.placementTest?.targetLevel || fallbackLevel
    );
    setWeightSplitOption("70-30");
    setExamDuration(70);
    setCustomPrompt("");
    setIsCreateTestModalOpen(true);
    setOpenKebabMenuId(null);
  };

  // Submit AI CEFR Test Generation
  const handleGenerateAITest = async () => {
    if (!activeCandidateForTest) return;
    if (!selectedTargetLevel) {
      swalWarning("Please select a target CEFR level for the exam.");
      return;
    }
    setIsGeneratingTest(true);

    try {
      const response = await fetchWithAuth(`${serverUrl}/api/level-test/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentId: activeCandidateForTest._id,
          classId: placementClassId,
          targetLevel: selectedTargetLevel,
          weightSplitOption,
          duration: Number(examDuration) || 70,
          userPrompt: customPrompt.trim()
        })
      });

      if (!response) return;
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to generate AI CEFR Exam");
      }

      setIsCreateTestModalOpen(false);
      swalSuccess(
        "Placement exam generated successfully! You can now download the .docx question paper directly from the candidate card."
      );
      fetchCandidates(true);
    } catch (err) {
      console.error("AI Generation error:", err);
      swalError(err.message || "Failed to generate AI Placement Test");
    } finally {
      setIsGeneratingTest(false);
    }
  };



  // Open Oral Viva Modal
  const handleOpenOralModal = (candidate) => {
    setActiveCandidateForOral(candidate);
    const existingViva = candidate.placementTest?.oralInquiryAssessment;
    setOralScore("");
    setOralNotes(existingViva?.notes || "");
    setOralRecommendation(existingViva?.recommendation || "");
    setIsOralModalOpen(true);
    setOpenKebabMenuId(null);
  };

  // Submit Oral Inquiry Evaluation (with optional continuous grading to next candidate)
  const handleSaveOralInquiry = async (goToNext = false) => {
    if (!activeCandidateForOral?.placementTest?.testId) return;

    setIsSavingOral(true);
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/level-test/oral-inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          testId: activeCandidateForOral.placementTest.testId,
          oralScore: Number(oralScore),
          notes: oralNotes.trim(),
          recommendation: oralRecommendation.trim()
        })
      });

      if (!response) return;
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to record oral inquiry");
      }

      swalSuccess(`Oral evaluation recorded for ${activeCandidateForOral.firstName} ${activeCandidateForOral.lastName}!`);
      await fetchCandidates(true);

      if (goToNext) {
        // Find next student awaiting viva in the current list
        const nextCandidate = filteredCandidates.find(
          (c) => c._id !== activeCandidateForOral._id && getCandidateStage(c) === "viva_pending"
        );
        if (nextCandidate) {
          handleOpenOralModal(nextCandidate);
          return;
        }
      }

      setIsOralModalOpen(false);
    } catch (err) {
      console.error("Save oral error:", err);
      swalError(err.message || "Failed to save oral inquiry");
    } finally {
      setIsSavingOral(false);
    }
  };

  // Download Question Paper (.docx)
  const handleDownloadPaper = async (testId) => {
    if (!testId) return;
    setOpenKebabMenuId(null);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/level-test/download-paper/${testId}`
      );
      if (!response || !response.ok) {
        throw new Error("Failed to retrieve question paper download link");
      }
      const data = await response.json();
      if (data.url) {
        const link = document.createElement("a");
        link.href = data.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download = data.fileName || "QuestionPaper.docx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error("Download paper error:", err);
      swalError(err.message || "Failed to download question paper");
    }
  };

  // Open Upload Answer Paper Modal
  const handleOpenUploadModal = (candidate) => {
    setActiveCandidateForUpload(candidate);
    setAnswerPaperFile(null);
    setUploadGradingResult(null);
    setIsUploadModalOpen(true);
    setOpenKebabMenuId(null);
  };

  // Submit Answer Paper & Trigger AssessQ AI Grading (Handles both Online & Batch Modes)
  const handleUploadAnswerPaper = async (e) => {
    e.preventDefault();
    if (!activeCandidateForUpload?.placementTest?.testId || !answerPaperFile) {
      swalWarning("Please select a valid .docx or .pdf answer paper to upload.");
      return;
    }

    setIsUploadingAndGrading(true);
    setUploadGradingResult(null);

    try {
      const formData = new FormData();
      formData.append("file", answerPaperFile);
      formData.append("testId", activeCandidateForUpload.placementTest.testId);
      formData.append("studentId", activeCandidateForUpload._id);

      const response = await fetchWithAuth(`${serverUrl}/api/level-test/upload-answer-paper`, {
        method: "POST",
        body: formData
      });

      if (!response) return;
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to upload and grade answer paper");
      }

      const resData = await response.json();
      setUploadGradingResult(resData);

      // Close upload modal immediately and reset uploaded file
      setIsUploadModalOpen(false);
      setAnswerPaperFile(null);
      setActiveCandidateForUpload(null);

      // Professional message guiding instructor to return later, with email notice and refresh option
      swalSuccess(
        "Answer paper submitted successfully! AI evaluation is in progress. Please check back in a short while—you will receive an email notification once grading is completed. You can also use the 'Refresh Results' button to check the latest grading status."
      );

      fetchCandidates(true);
    } catch (err) {
      console.error("Upload & grading error:", err);
      swalError(err.message || "Failed to upload and grade answer paper");
    } finally {
      setIsUploadingAndGrading(false);
    }
  };

  // Open Diagnostic Report Modal
  const handleOpenDiagnostic = async (testId) => {
    setOpenKebabMenuId(null);
    setIsDiagnosticModalOpen(true);
    setLoadingDiagnostic(true);
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/level-test/details/${testId}`);
      if (!response) return;
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to load diagnostic details");
      }
      const data = await response.json();
      setDiagnosticData(data);
    } catch (err) {
      console.error("Diagnostic fetch error:", err);
      swalError(err.message || "Failed to load diagnostic report");
      setIsDiagnosticModalOpen(false);
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  // View Detailed Exam Results in ViewResults (/instructor/results/:answerPaperId)
  const [loadingResultsForId, setLoadingResultsForId] = useState(null);

  const handleViewResults = async (candidate) => {
    const pt = candidate?.placementTest;
    const testId = pt?.testId;

    if (!pt && !testId) {
      swalWarning("No placement test found for this candidate.");
      return;
    }

    // 1. If answerPaperId is already cached
    if (pt?.answerPaperId) {
      window.open(`/instructor/results/${pt.answerPaperId}`, "_blank");
      return;
    }

    setLoadingResultsForId(candidate._id);
    try {
      // 2. Fetch answer papers for this test
      const response = await fetchWithAuth(`${serverUrl}/api/answer-paper/get-all/${testId}?all=true`);
      if (response && response.ok) {
        const resData = await response.json();
        const papers = Array.isArray(resData) ? resData : resData.answerPapers || resData.data || [];
        const studentId = candidate._id;
        const matchingPaper = papers.find(
          (p) => String(p.studentId?._id || p.studentId) === String(studentId)
        ) || papers[0];

        if (matchingPaper?._id) {
          // Cache in local candidates state
          setCandidates((prev) =>
            prev.map((c) =>
              c._id === candidate._id
                ? { ...c, placementTest: { ...c.placementTest, answerPaperId: matchingPaper._id } }
                : c
            )
          );
          window.open(`/instructor/results/${matchingPaper._id}`, "_blank");
          return;
        }
      }

      // 3. Fallback: check diagnostic details
      const detailsRes = await fetchWithAuth(`${serverUrl}/api/level-test/details/${testId}`);
      if (detailsRes && detailsRes.ok) {
        const dData = await detailsRes.json();
        const apId = dData?.answerPaperId || dData?.test?.answerPaperId;
        if (apId) {
          setCandidates((prev) =>
            prev.map((c) =>
              c._id === candidate._id
                ? { ...c, placementTest: { ...c.placementTest, answerPaperId: apId } }
                : c
            )
          );
          window.open(`/instructor/results/${apId}`, "_blank");
          return;
        }
      }

      swalWarning("Answer paper has not been uploaded or graded yet. Please upload the answer paper first.");
    } catch (err) {
      console.error("Failed to load answer paper for results:", err);
      swalError("Could not open exam results at this time.");
    } finally {
      setLoadingResultsForId(null);
    }
  };



  // Delete Placement Exam (Reset Candidate)
  const handleDeleteExam = async (testId, candidate) => {
    setOpenKebabMenuId(null);
    if (!testId) return;
    const candidateName = candidate
      ? `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim()
      : "candidate";

    const result = await swalConfirm(
      "Delete Placement Exam?",
      `Are you sure you want to permanently delete the placement exam for ${candidateName}? This will remove the question paper, answers, and scores, resetting the candidate for a new exam.`,
      "Yes, Delete Exam",
      "Cancel"
    );

    if (result && (result.isConfirmed || result.value)) {
      try {
        const response = await fetchWithAuth(`${serverUrl}/api/level-test/exam/${testId}`, {
          method: "DELETE"
        });
        if (!response) return;
        const resData = await response.json();
        if (!response.ok || !resData.success) {
          throw new Error(resData.message || "Failed to delete placement exam");
        }
        swalSuccess(resData.message || "Placement exam deleted successfully");
        if (isDiagnosticModalOpen) setIsDiagnosticModalOpen(false);
        fetchCandidates(true);
      } catch (err) {
        console.error("Delete exam error:", err);
        swalError(err.message || "Could not delete placement exam");
      }
    }
  };

  // Remove Candidate from Level Test Zone
  const handleDeleteCandidate = async (candidate) => {
    setOpenKebabMenuId(null);
    if (!candidate?._id) return;
    const candidateName =
      `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() || "this candidate";

    const result = await swalConfirm(
      "Remove Candidate?",
      `Are you sure you want to remove ${candidateName} from the Level Test Zone? All associated placement exams will be deleted.`,
      "Yes, Remove Candidate",
      "Cancel"
    );

    if (result && (result.isConfirmed || result.value)) {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/level-test/candidate/${candidate._id}`,
          {
            method: "DELETE"
          }
        );
        if (!response) return;
        const resData = await response.json();
        if (!response.ok || !resData.success) {
          throw new Error(resData.message || "Failed to remove candidate");
        }
        swalSuccess(resData.message || "Candidate removed successfully");
        fetchCandidates(true);
      } catch (err) {
        console.error("Remove candidate error:", err);
        swalError(err.message || "Could not remove candidate");
      }
    }
  };

  return (
    <div className="lpz-container">
      {/* 1. Header Section with Refresh Results button */}
      <div className="lpz-header">
        <div className="lpz-header-left">
          <div className="lpz-header-icon">
            <i className="fa-solid fa-graduation-cap"></i>
          </div>
          <div>
            <h1 className="lpz-header-title">
              Level Test Zone
              <span className="cefr-badge cefr-badge-sm" style={{ background: "#fff7ed", color: "var(--color-1, #f09a21)", border: "1px solid #fed7aa" }}>
                CEFR Placement Standards
              </span>
            </h1>
            <p className="lpz-header-subtitle">
              Comprehensive transfer student admission portal: AI CEFR exams, oral inquiry assessment, and regular batch placement.
            </p>
          </div>
        </div>

        <div className="lpz-header-actions">
          <button
            className="lpz-btn lpz-btn-secondary"
            onClick={() => fetchCandidates()}
            disabled={loading}
            title="Reload candidates list"
          >
            <i className={`fa-solid fa-users ${loading ? "fa-spin" : ""}`}></i>
            Sync Candidates
          </button>
        </div>
      </div>

      {/* 2. Interactive Pipeline Metric Cards */}
      <div className="lpz-stats-grid">
        <div
          className={`lpz-stat-card ${selectedStageFilter === "all" ? "active" : ""}`}
          onClick={() => setSelectedStageFilter("all")}
          title="Show all candidates"
        >
          <div>
            <div className="lpz-stat-label">Total Candidates</div>
            <div className="lpz-stat-value">{stats.total}</div>
          </div>
          <div className="lpz-stat-icon-wrap" style={{ background: "#eff6ff", color: "#2563eb" }}>
            <i className="fa-solid fa-users"></i>
          </div>
        </div>

        <div
          className={`lpz-stat-card ${selectedStageFilter === "need_exam" ? "active" : ""}`}
          onClick={() => setSelectedStageFilter("need_exam")}
          title="Filter candidates needing exam creation"
        >
          <div>
            <div className="lpz-stat-label">Exams Needed</div>
            <div className="lpz-stat-value">{stats.needExam}</div>
          </div>
          <div className="lpz-stat-icon-wrap" style={{ background: "#fff7ed", color: "var(--color-1, #f09a21)" }}>
            <i className="fa-solid fa-wand-magic-sparkles"></i>
          </div>
        </div>

        <div
          className={`lpz-stat-card ${selectedStageFilter === "viva_pending" ? "active" : ""}`}
          onClick={() => setSelectedStageFilter("viva_pending")}
          title="Filter candidates awaiting teacher oral viva"
        >
          <div>
            <div className="lpz-stat-label">Oral Viva Pending</div>
            <div className="lpz-stat-value">{stats.vivaPending}</div>
          </div>
          <div className="lpz-stat-icon-wrap" style={{ background: "#fdf4ff", color: "var(--color-2, #da1c5c)" }}>
            <i className="fa-solid fa-microphone-lines"></i>
          </div>
        </div>

        <div
          className={`lpz-stat-card ${selectedStageFilter === "ready_decision" ? "active" : ""}`}
          onClick={() => setSelectedStageFilter("ready_decision")}
          title="Filter candidates evaluated and awaiting Admin level assignment"
        >
          <div>
            <div className="lpz-stat-label">Ready for Admin</div>
            <div className="lpz-stat-value">{stats.readyDecision}</div>
          </div>
          <div className="lpz-stat-icon-wrap" style={{ background: "#f0fdf4", color: "#16a34a" }}>
            <i className="fa-solid fa-paper-plane"></i>
          </div>
        </div>
      </div>

      {/* 4. Toolbar with Search, CEFR Level Dropdown Selector, and View Mode Toggle */}
      <div className="lpz-toolbar">
        <div className="lpz-toolbar-left" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Search Box */}
          <div className="lpz-search-box">
            <i className="fa-solid fa-magnifying-glass lpz-search-icon"></i>
            <input
              type="text"
              placeholder="Search by candidate name, email, or reference ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="lpz-search-clear"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>
        </div>

        <div className="lpz-toolbar-right" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Level Dropdown Selector */}
          <div className="lpz-dropdown-wrapper">
            <i className="fa-solid fa-layer-group lpz-dropdown-icon"></i>
            <select
              className="lpz-level-select"
              value={selectedLevelFilter}
              onChange={(e) => setSelectedLevelFilter(e.target.value)}
              aria-label="Filter by Level"
            >
              {levelOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 6. Candidate Listing */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem" }}>
          <Loader style={{ fontSize: "3rem" }} />
          <p style={{ marginTop: "1rem", color: "#64748b" }}>Loading Level Test Candidates...</p>
        </div>
      ) : filteredCandidates.length === 0 ? (
        <div
          style={{
            background: "#ffffff",
            border: "1.5px dashed #cbd5e1",
            borderRadius: "12px",
            padding: "3.5rem 2rem",
            textAlign: "center"
          }}
        >
          <div style={{ fontSize: "2.5rem", color: "#94a3b8", marginBottom: "0.75rem" }}>
            <i className="fa-solid fa-user-graduate"></i>
          </div>
          <h3 style={{ margin: "0 0 0.5rem 0", color: "#1e293b" }}>No Placement Candidates Found</h3>
          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
            {searchTerm || selectedLevelFilter !== "All" || selectedStageFilter !== "all"
              ? "No students match the active search, CEFR level, or stage filters."
              : "All candidates have completed their placement evaluation. When new transfer students register, they will appear here."}
          </p>
        </div>
      ) : (
        /* Modern Cards Grid */
        <div className="lpz-cards-grid">
          {filteredCandidates.map((candidate) => {
            const hasTest = !!candidate.placementTest;
            const pt = candidate.placementTest;
            const stage = getCandidateStage(candidate);

            // Determine 4-step stepper states
            const step1Done = hasTest && stage !== "generating" && stage !== "failed";
            const step2Done = pt?.writtenScore != null || pt?.paperStatus === "Graded";
            const step2Pulsing = pt?.paperStatus === "Requested";
            const step3Done = pt?.oralStatus === "Completed";
            const step4Done = pt?.placementStatus === "Approved";

            return (
              <div
                key={candidate._id}
                className="lpz-candidate-card"
              >
                <div>
                  {/* Top Header Row */}
                  <div className="lpz-card-top">
                    <div className="lpz-card-user">
                      <div className="lpz-avatar-initials">
                        {candidate.firstName?.[0] || ""}{candidate.lastName?.[0] || ""}
                      </div>
                      <div className="lpz-user-text">
                        <h3 className="lpz-user-name" title={`${candidate.firstName} ${candidate.lastName}`}>
                          {candidate.firstName} {candidate.lastName}
                        </h3>
                        {candidate.referenceId && (
                          <div className="lpz-user-sub" title={`Ref: ${candidate.referenceId}`}>
                            Ref: {candidate.referenceId}
                          </div>
                        )}
                        <div
                          className="lpz-user-sub"
                          title={candidate.email || "-"}
                        >
                          {candidate.email || "-"}
                        </div>
                        {candidate.phoneNumber && (
                          <div
                            className="lpz-user-sub"
                            title={formatPhoneNumber(candidate.phoneNumber)}
                          >
                            {formatPhoneNumber(candidate.phoneNumber)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="lpz-card-top-right">
                      {/* Dropdown Kebab Menu */}
                      <div className="lpz-kebab-wrap">
                        <button
                          className="lpz-btn-kebab"
                          onClick={() => setOpenKebabMenuId(openKebabMenuId === candidate._id ? null : candidate._id)}
                          title="Actions menu"
                        >
                          <i className="fa-solid fa-ellipsis-vertical"></i>
                        </button>

                        {openKebabMenuId === candidate._id && (
                          <div className="lpz-menu-dropdown" ref={kebabRef}>
                            {pt && (
                              <>
                                {(pt.testPaperURL || pt.docxURL) && (
                                  <button
                                    className="lpz-menu-item"
                                    onClick={() => handleDownloadPaper(pt.testId)}
                                  >
                                    <i className="fa-solid fa-file-word" style={{ color: "#2563eb" }}></i>
                                    Download Paper (.docx)
                                  </button>
                                )}
                                <button
                                  className="lpz-menu-item"
                                  onClick={() => handleOpenUploadModal(candidate)}
                                >
                                  <i className="fa-solid fa-cloud-arrow-up" style={{ color: "var(--color-1, #f09a21)" }}></i>
                                  {pt.writtenScore != null ? "Regrade Answer Paper" : "Upload Answer Paper"}
                                </button>
                                <button
                                  className="lpz-menu-item"
                                  onClick={() => handleOpenOralModal(candidate)}
                                >
                                  <i className="fa-solid fa-microphone" style={{ color: "var(--color-2, #da1c5c)" }}></i>
                                  {pt.oralStatus === "Completed" ? "Edit Oral Viva" : "Conduct Oral Viva"}
                                </button>
                                <button
                                  className="lpz-menu-item"
                                  onClick={() => handleOpenDiagnostic(pt.testId)}
                                >
                                  <i className="fa-solid fa-chart-pie" style={{ color: "#0f766e" }}></i>
                                  Diagnostic Scorecard
                                </button>
                                {hasTest && (pt.writtenScore != null || pt.paperStatus === "Graded") && (
                                  <button
                                    className="lpz-menu-item"
                                    onClick={() => {
                                      setOpenKebabMenuId(null);
                                      handleViewResults(candidate);
                                    }}
                                    title="Open detailed question-wise results and rubric feedback"
                                  >
                                    <i className="fa-solid fa-square-poll-vertical" style={{ color: "#0284c7" }}></i>
                                    View Level Exam Results
                                  </button>
                                )}
                                <div className="lpz-menu-divider"></div>
                                <button
                                  className="lpz-menu-item danger"
                                  onClick={() => handleDeleteExam(pt.testId, candidate)}
                                >
                                  <i className="fa-solid fa-trash-can"></i>
                                  Delete Placement Exam
                                </button>
                              </>
                            )}
                            <button
                              className="lpz-menu-item danger"
                              onClick={() => handleDeleteCandidate(candidate)}
                            >
                              <i className="fa-solid fa-user-xmark"></i>
                              Remove Candidate
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 4-Step Visual Progress Stepper */}
                  <div className="lpz-stepper">
                    <div className={`lpz-step-item ${step1Done ? "completed" : "active"}`}>
                      <div className="lpz-step-dot">
                        <i className={`fa-solid ${step1Done ? "fa-check" : "fa-1"}`}></i>
                      </div>
                      <span className="lpz-step-label">Exam</span>
                    </div>

                    <div className={`lpz-step-item ${step2Done ? "completed" : step2Pulsing ? "pulsing" : step1Done ? "active" : ""}`}>
                      <div className="lpz-step-dot">
                        <i className={`fa-solid ${step2Done ? "fa-check" : step2Pulsing ? "fa-spinner fa-spin" : "fa-2"}`}></i>
                      </div>
                      <span className="lpz-step-label">Written</span>
                    </div>

                    <div className={`lpz-step-item ${step3Done ? "completed" : step2Done ? "active" : ""}`}>
                      <div className="lpz-step-dot">
                        <i className={`fa-solid ${step3Done ? "fa-check" : "fa-3"}`}></i>
                      </div>
                      <span className="lpz-step-label">Viva</span>
                    </div>

                    <div className={`lpz-step-item ${step4Done ? "completed" : step3Done ? "active" : ""}`}>
                      <div className="lpz-step-dot">
                        <i className={`fa-solid ${step4Done ? "fa-check" : "fa-4"}`}></i>
                      </div>
                      <span className="lpz-step-label">Placed</span>
                    </div>
                  </div>

                  {/* Test Box & Scores */}
                  {hasTest ? (
                    <div className="lpz-test-box">
                      <div className="lpz-test-box-header">
                        <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          <i className="fa-solid fa-file-lines" style={{ color: "var(--color-1, #f09a21)" }}></i>
                          <strong>{pt.testName || "Placement Exam"}</strong>
                        </span>
                        <span className="lpz-status-pill lpz-status-eval" style={{ fontSize: "0.72rem" }}>
                          Split: {pt.weightSplit?.writtenWeight || 70}/{pt.weightSplit?.oralWeight || 30}
                        </span>
                      </div>

                      <div className="lpz-scores-grid">
                        <div className="lpz-score-tile">
                          <div className="lpz-score-tile-label">Written Exam</div>
                          <div className="lpz-score-tile-value">
                            {pt.paperStatus === "Requested" ? (
                              <span style={{ fontSize: "0.74rem", color: "#0f766e" }}>
                                Grading...
                              </span>
                            ) : pt.writtenScore != null ? (
                              <button
                                type="button"
                                onClick={() => handleViewResults(candidate)}
                                className="lpz-score-link-btn"
                                title="Click to view detailed results in Results Viewer"
                              >
                                <span>{pt.writtenScore} / {pt.writtenMaxScore}</span>
                                <i className={`fa-solid ${loadingResultsForId === candidate._id ? "fa-spinner fa-spin" : "fa-arrow-up-right-from-square"}`} style={{ fontSize: "0.68rem" }}></i>
                              </button>
                            ) : (
                              <span style={{ fontSize: "0.76rem", color: "#f59e0b" }}>Pending</span>
                            )}
                          </div>
                        </div>

                        <div className="lpz-score-tile">
                          <div className="lpz-score-tile-label">Oral Viva</div>
                          <div className="lpz-score-tile-value">
                            {pt.oralStatus === "Completed" ? (
                              `${pt.oralScore} / ${pt.oralMaxScore}`
                            ) : (
                              <span style={{ fontSize: "0.76rem", color: "#f59e0b" }}>Pending</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Composite Score Pill if available */}
                      {pt.writtenScore != null && pt.oralStatus === "Completed" && (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "#ffffff",
                          border: "1px solid #bbf7d0",
                          borderRadius: "6px",
                          padding: "4px 8px",
                          fontSize: "0.75rem",
                          color: "#166534",
                          fontWeight: 700
                        }}>
                          <span>Composite Score:</span>
                          <span>
                            {pt.writtenScore + (pt.oralScore || 0)} / {pt.writtenMaxScore + pt.oralMaxScore} (
                            {Math.round(((pt.writtenScore + (pt.oralScore || 0)) / (pt.writtenMaxScore + pt.oralMaxScore)) * 100)}%)
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "#f8fafc",
                        border: "1px dashed #cbd5e1",
                        borderRadius: "8px",
                        padding: "0.9rem",
                        marginBottom: "0.85rem",
                        textAlign: "center"
                      }}
                    >
                      {stage === "generating" ? (
                        <p style={{ margin: 0, fontSize: "0.82rem", color: "#0f766e", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                          <i className="fa-solid fa-gear fa-spin" style={{ color: "var(--color-1, #f09a21)" }}></i>
                          Generating exam paper in background — check back in a moment.
                        </p>
                      ) : stage === "failed" ? (
                        <p style={{ margin: 0, fontSize: "0.82rem", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                          <i className="fa-solid fa-triangle-exclamation"></i>
                          Exam generation failed. Please try creating a new exam.
                        </p>
                      ) : (
                        <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b" }}>
                          <i className="fa-solid fa-circle-exclamation" style={{ color: "var(--color-1, #f09a21)", marginRight: "6px" }}></i>
                          No placement exam generated yet.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Card Primary Contextual Action Row */}
                <div className="lpz-card-actions">
                  {(stage === "need_exam" || stage === "failed") && (
                    <button
                      className="lpz-btn lpz-btn-primary lpz-btn-contextual"
                      onClick={() => handleOpenCreateTest(candidate)}
                    >
                      <i className="fa-solid fa-wand-magic-sparkles"></i>
                      {stage === "failed" ? "Retry Exam Generation" : "Create Placement Exam"}
                    </button>
                  )}

                  {stage === "generating" && (
                    <button
                      className="lpz-btn lpz-btn-primary lpz-btn-contextual"
                      disabled
                      style={{ opacity: 0.75, cursor: "not-allowed", background: "linear-gradient(135deg, #0f766e, #0d9488)" }}
                    >
                      <i className="fa-solid fa-gear fa-spin"></i>
                      Generating Exam Paper...
                    </button>
                  )}

                  {stage === "grading_pending" && (
                    <>
                      {(pt?.testPaperURL || pt?.docxURL) && (
                        <button
                          className="lpz-btn lpz-btn-secondary lpz-btn-sm"
                          onClick={() => handleDownloadPaper(pt.testId)}
                          title="Download blank Question Paper (.docx)"
                        >
                          <i className="fa-solid fa-download"></i> .docx
                        </button>
                      )}
                      <button
                        className="lpz-btn lpz-btn-primary lpz-btn-contextual"
                        onClick={() => handleOpenUploadModal(candidate)}
                      >
                        <i className="fa-solid fa-cloud-arrow-up"></i>
                        Upload Answer Paper
                      </button>
                    </>
                  )}

                  {stage === "batch_requested" && (
                    <button
                      className="lpz-btn lpz-btn-warning lpz-btn-contextual lpz-pulse-glow"
                      onClick={handleRefreshResults}
                      title="Vertex AI batch grading is running in the background. Click to check if it's completed."
                    >
                      <i className={`fa-solid fa-rotate-right ${refreshing ? "fa-spin" : ""}`}></i>
                      Grading in progress... (Click to Refresh)
                    </button>
                  )}

                  {stage === "viva_pending" && (
                    <div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
                      <button
                        className="lpz-btn lpz-btn-secondary lpz-btn-sm"
                        onClick={() => handleViewResults(candidate)}
                        title="View question-wise results for the written exam"
                      >
                        <i className={`fa-solid ${loadingResultsForId === candidate._id ? "fa-spinner fa-spin" : "fa-square-poll-vertical"}`}></i>
                        Results
                      </button>
                      <button
                        className="lpz-btn lpz-btn-brand-2 lpz-btn-contextual"
                        onClick={() => handleOpenOralModal(candidate)}
                        style={{ flex: 1 }}
                      >
                        <i className="fa-solid fa-microphone"></i>
                        Conduct Oral Viva
                      </button>
                    </div>
                  )}

                  {stage === "ready_decision" && (
                    <div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
                      <button
                        className="lpz-btn lpz-btn-secondary lpz-btn-sm"
                        onClick={() => handleViewResults(candidate)}
                        title="View question-wise results for the written exam"
                      >
                        <i className={`fa-solid ${loadingResultsForId === candidate._id ? "fa-spinner fa-spin" : "fa-square-poll-vertical"}`}></i>
                        Results
                      </button>
                      <button
                        className="lpz-btn lpz-btn-secondary lpz-btn-contextual"
                        onClick={() => handleOpenDiagnostic(pt.testId)}
                        style={{ flex: 1 }}
                        title="Evaluation complete. View full scorecard and teacher feedback submitted to Administrator"
                      >
                        <i className="fa-solid fa-file-circle-check" style={{ color: "var(--color-1, #f09a21)" }}></i>
                        Report Sent to Admin
                      </button>
                    </div>
                  )}

                  {stage === "completed" && (
                    <div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
                      <button
                        className="lpz-btn lpz-btn-secondary lpz-btn-sm"
                        onClick={() => handleViewResults(candidate)}
                        title="View question-wise results for the written exam"
                      >
                        <i className={`fa-solid ${loadingResultsForId === candidate._id ? "fa-spinner fa-spin" : "fa-square-poll-vertical"}`}></i>
                        Results
                      </button>
                      <button
                        className="lpz-btn lpz-btn-secondary lpz-btn-contextual"
                        onClick={() => handleOpenDiagnostic(pt.testId)}
                        style={{ flex: 1 }}
                      >
                        <i className="fa-solid fa-chart-pie"></i>
                        Diagnostic Report
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* MODAL 1: CREATE PLACEMENT TEST (AI GENERATOR)                    */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={isCreateTestModalOpen}
        onClose={() => !isGeneratingTest && setIsCreateTestModalOpen(false)}
        maxWidth="700px"
      >
        <div className="lpz-modal-body">
          <div className="lpz-modal-header">
            <div className="lpz-modal-header-icon">
              <i className="fa-solid fa-wand-magic-sparkles"></i>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}>
                Create Level Test
              </h3>
              <p style={{ margin: "0.2rem 0 0 0", fontSize: "0.82rem", color: "#64748b" }}>
                Candidate: <strong>{activeCandidateForTest?.firstName} {activeCandidateForTest?.lastName}</strong> | Initial Level: <strong>{activeCandidateForTest?.level || "Unassigned (Level Test Required)"}</strong>
              </p>
            </div>
          </div>

          {/* Settings: Weight Split & Duration */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
              Assessment Weight Split:
            </label>
            <div className="lpz-split-selector">
              <div
                className={`lpz-split-card ${weightSplitOption === "70-30" ? "active" : ""}`}
                onClick={() => setWeightSplitOption("70-30")}
              >
                <div style={{ fontWeight: 700, fontSize: "0.86rem", color: "#0f172a" }}>
                  70% Written / 30% Oral (Standard)
                </div>
                <div style={{ fontSize: "0.74rem", color: "#64748b" }}>
                  70 pts written exam + 30 pts teacher oral viva
                </div>
              </div>

              <div
                className={`lpz-split-card ${weightSplitOption === "80-20" ? "active" : ""}`}
                onClick={() => setWeightSplitOption("80-20")}
              >
                <div style={{ fontWeight: 700, fontSize: "0.86rem", color: "#0f172a" }}>
                  80% Written / 20% Oral (Comprehensive)
                </div>
                <div style={{ fontSize: "0.74rem", color: "#64748b" }}>
                  80 pts written exam + 20 pts teacher oral viva
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                Exam Duration (Minutes):
              </label>
              <input
                type="number"
                min="15"
                max="180"
                value={examDuration}
                onChange={(e) => setExamDuration(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.55rem 0.8rem",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "0.88rem"
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                Target Level: <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                value={selectedTargetLevel}
                onChange={(e) => setSelectedTargetLevel(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.55rem 0.8rem",
                  borderRadius: "8px",
                  border: "1.5px solid var(--color-1, #f09a21)",
                  background: "#fff",
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  color: "#0f172a",
                  cursor: "pointer"
                }}
              >
                {levelOptions
                  .filter((l) => l.value !== "All")
                  .map((lvl) => (
                    <option key={lvl.value} value={lvl.value}>
                      {lvl.label}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* AI Mode: Prompt */}
          <div style={{ marginBottom: "1.25rem" }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
              Custom Instructions / Focus Topics <span style={{ color: "#94a3b8" }}>(optional)</span>:
            </label>
            <textarea
              rows={3}
              placeholder="e.g. Focus on subordinate clauses (weil/dass), past tense conjugation, and workplace emails..."
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              style={{
                width: "100%",
                padding: "0.6rem 0.8rem",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "0.84rem",
                resize: "vertical"
              }}
            />
          </div>

          {/* Modal Footer */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid #e2e8f0" }}>
            <button
              type="button"
              className="lpz-btn lpz-btn-secondary"
              onClick={() => setIsCreateTestModalOpen(false)}
              disabled={isGeneratingTest}
            >
              Cancel
            </button>

            <button
              type="button"
              className="lpz-btn lpz-btn-primary"
              onClick={handleGenerateAITest}
              disabled={isGeneratingTest}
            >
              {isGeneratingTest ? (
                <><i className="fa-solid fa-spinner fa-spin"></i> Generating CEFR Exam with AI...</>
              ) : (
                <><i className="fa-solid fa-wand-magic-sparkles"></i> Generate AI Exam</>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* MODAL 2: UPLOAD ANSWER PAPER & ASSESSQ AI GRADING                */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => !isUploadingAndGrading && setIsUploadModalOpen(false)}
        maxWidth="580px"
      >
        <div className="lpz-modal-body">
          <div className="lpz-modal-header">
            <div className="lpz-modal-header-icon">
              <i className="fa-solid fa-cloud-arrow-up"></i>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}>
                Upload & Grade Answer Paper
              </h3>
              <p style={{ margin: "0.2rem 0 0 0", fontSize: "0.82rem", color: "#64748b" }}>
                Candidate: <strong>{activeCandidateForUpload?.firstName} {activeCandidateForUpload?.lastName}</strong> | Level: <strong>{activeCandidateForUpload?.placementTest?.targetLevel}</strong>
              </p>
            </div>
          </div>

          <form onSubmit={handleUploadAnswerPaper}>
            {/* Download Link Reminder */}
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "0.75rem 1rem",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <div>
                <div style={{ fontSize: "0.84rem", fontWeight: 600, color: "#1e293b" }}>
                  Question Paper (.docx)
                </div>
                <div style={{ fontSize: "0.74rem", color: "#64748b" }}>
                  Need a blank copy for the student?
                </div>
              </div>
              {activeCandidateForUpload?.placementTest?.testPaperURL && (
                <button
                  type="button"
                  className="lpz-btn lpz-btn-secondary lpz-btn-sm"
                  onClick={() => handleDownloadPaper(activeCandidateForUpload?.placementTest?.testId)}
                >
                  <i className="fa-solid fa-file-word"></i> Download .docx
                </button>
              )}
            </div>

            {/* File Drag & Drop Input */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", fontSize: "0.83rem", fontWeight: 600, color: "#334155", marginBottom: "0.4rem" }}>
                Candidate Answer Paper (.docx / .pdf):
              </label>

              <div
                style={{
                  border: "2px dashed #cbd5e1",
                  borderRadius: "10px",
                  padding: "1.5rem 1.25rem",
                  textAlign: "center",
                  background: answerPaperFile ? "#f0fdf4" : "#ffffff",
                  borderColor: answerPaperFile ? "#22c55e" : "#cbd5e1",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
                onClick={() => document.getElementById("lpz-answer-file-input")?.click()}
              >
                <input
                  id="lpz-answer-file-input"
                  type="file"
                  accept=".docx,.doc,.pdf"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setAnswerPaperFile(e.target.files[0]);
                    }
                  }}
                />

                {answerPaperFile ? (
                  <div>
                    <div style={{ fontSize: "1.8rem", color: "#16a34a", marginBottom: "0.4rem" }}>
                      <i className="fa-solid fa-file-circle-check"></i>
                    </div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                      {answerPaperFile.name}
                    </div>
                    <div style={{ fontSize: "0.76rem", color: "#64748b", marginTop: "0.2rem" }}>
                      {(answerPaperFile.size / 1024).toFixed(1)} KB — Click to change file
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: "1.8rem", color: "#94a3b8", marginBottom: "0.4rem" }}>
                      <i className="fa-solid fa-file-arrow-up"></i>
                    </div>
                    <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.9rem" }}>
                      Click to browse or drop candidate answer paper here
                    </div>
                    <div style={{ fontSize: "0.76rem", color: "#64748b", marginTop: "0.25rem" }}>
                      Accepts Word Document (.docx) or PDF (.pdf)
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Results Preview if Graded */}
            {uploadGradingResult && (
              <div
                style={{
                  background: uploadGradingResult.isBatch ? "#f0fdfa" : "#f0fdf4",
                  border: `1.5px solid ${uploadGradingResult.isBatch ? "#99f6e4" : "#bbf7d0"}`,
                  borderRadius: "8px",
                  padding: "0.9rem",
                  marginBottom: "1rem",
                  textAlign: "center"
                }}
              >
                {uploadGradingResult.isBatch ? (
                  <>
                    <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0f766e" }}>
                      <i className="fa-solid fa-clock" style={{ marginRight: "6px" }}></i>
                      Answer Paper Queued for AI Grading
                    </div>
                    <p style={{ margin: "0.4rem 0 0 0", fontSize: "0.78rem", color: "#134e4a" }}>
                      AI Grading is in progress. Use the <strong>Refresh Results</strong> button to check status.
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#166534" }}>
                      <i className="fa-solid fa-circle-check" style={{ marginRight: "6px" }}></i>
                      AI Grading Completed!
                    </div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#15803d", margin: "0.25rem 0" }}>
                      {uploadGradingResult.writtenScore} / {uploadGradingResult.writtenMaxScore} Marks
                    </div>
                    <div style={{ fontSize: "0.76rem", color: "#166534" }}>
                      You can now conduct the Oral Viva or review the Diagnostic Scorecard.
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Modal Footer */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid #e2e8f0" }}>
              <button
                type="button"
                className="lpz-btn lpz-btn-secondary"
                onClick={() => setIsUploadModalOpen(false)}
                disabled={isUploadingAndGrading}
              >
                Close
              </button>
              <button
                type="submit"
                className="lpz-btn lpz-btn-primary"
                disabled={isUploadingAndGrading || !answerPaperFile}
              >
                {isUploadingAndGrading ? (
                  <><i className="fa-solid fa-spinner fa-spin"></i> Submitting & Grading...</>
                ) : (
                  <><i className="fa-solid fa-bolt"></i> Upload & Submit for Grading</>
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* MODAL 3: ORAL INQUIRY / VIVA VOCE (WITH CONTINUOUS GRADING)       */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={isOralModalOpen}
        onClose={() => !isSavingOral && setIsOralModalOpen(false)}
        maxWidth="660px"
      >
        <div className="lpz-modal-body">
          <div className="lpz-modal-header">
            <div className="lpz-modal-header-icon" style={{ background: "#fdf4ff", color: "var(--color-2, #da1c5c)", borderColor: "#f5d0fe" }}>
              <i className="fa-solid fa-microphone-lines"></i>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}>
                Oral Inquiry Assessment (Viva Voce)
              </h3>
              <p style={{ margin: "0.2rem 0 0 0", fontSize: "0.82rem", color: "#64748b" }}>
                Candidate: <strong>{activeCandidateForOral?.firstName} {activeCandidateForOral?.lastName}</strong> | Maximum: <strong>{activeCandidateForOral?.placementTest?.oralMaxScore || 30} Points</strong>
              </p>
            </div>
          </div>

          {(() => {
            const oralMax = activeCandidateForOral?.placementTest?.oralMaxScore || 30;
            const currentScoreNum = Number(oralScore || 0);
            const oralPct = Math.round((currentScoreNum / oralMax) * 100);

            return (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveOralInquiry(false);
                }}
              >
                {/* 1. Hero Score Card */}
                <div className="lpz-oral-hero-card">
                  <div className="lpz-oral-hero-header">
                    <div className="lpz-oral-hero-title">
                      <i className="fa-solid fa-award" style={{ color: "var(--color-1, #f09a21)", marginRight: "6px" }}></i>
                      Candidate Viva Score
                    </div>
                  </div>

                  <div className="lpz-oral-input-group">
                    <input
                      type="number"
                      min="0"
                      max={oralMax}
                      value={oralScore}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "") setOralScore("");
                        else setOralScore(Math.min(oralMax, Math.max(0, Number(val))));
                      }}
                      className="lpz-oral-big-input"
                      aria-label="Oral Viva Score"
                    />
                    <div className="lpz-oral-ceiling-label">/ {oralMax} Marks</div>
                    <div className="lpz-oral-pct-pill">{oralPct}%</div>
                  </div>
                </div>

                {/* 3. Placement Recommendation with Quick Suggestion Chips */}
                <div style={{ marginBottom: "0.85rem" }}>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                    Oral Recommendation:
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Recommended for Level A2.2"
                    value={oralRecommendation}
                    onChange={(e) => setOralRecommendation(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "0.55rem 0.8rem",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      fontSize: "0.85rem",
                      boxSizing: "border-box"
                    }}
                  />
                  <div className="lpz-chips-wrap">
                    {[
                      `Ready for Level ${activeCandidateForOral?.level || "Target"}`,
                      `Refresher in ${activeCandidateForOral?.level || "Target"} Recommended`,
                      `Passed with Distinction`,
                      `Further Language Support Needed`
                    ].map((chipText, cIdx) => (
                      <button
                        key={cIdx}
                        type="button"
                        className="lpz-chip-btn"
                        onClick={() => setOralRecommendation(chipText)}
                      >
                        + {chipText}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 4. Teacher Observations */}
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                    Teacher Observations & Notes:
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Candidate demonstrated natural conversation flow, strong listening comprehension, and responded clearly to prompt scenarios..."
                    value={oralNotes}
                    onChange={(e) => setOralNotes(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "0.55rem 0.8rem",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      fontSize: "0.84rem",
                      resize: "vertical",
                      boxSizing: "border-box"
                    }}
                  />
                </div>

                {/* Modal Footer with Continuous Grading "Save & Next" */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.75rem", borderTop: "1px solid #e2e8f0" }}>
                  <button
                    type="button"
                    className="lpz-btn lpz-btn-secondary"
                    onClick={() => setIsOralModalOpen(false)}
                    disabled={isSavingOral}
                  >
                    Cancel
                  </button>

                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      type="button"
                      className="lpz-btn lpz-btn-secondary"
                      onClick={() => handleSaveOralInquiry(true)}
                      disabled={isSavingOral}
                      title="Save this score and advance to next student awaiting viva"
                    >
                      <i className="fa-solid fa-forward-step"></i> Save & Next Student
                    </button>

                    <button
                      type="submit"
                      className="lpz-btn lpz-btn-primary"
                      disabled={isSavingOral}
                    >
                      {isSavingOral ? (
                        <><i className="fa-solid fa-spinner fa-spin"></i> Saving...</>
                      ) : (
                        <><i className="fa-solid fa-check"></i> Save & Close</>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            );
          })()}
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* MODAL 4: PLACEMENT DIAGNOSTIC REPORT                             */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={isDiagnosticModalOpen}
        onClose={() => setIsDiagnosticModalOpen(false)}
        maxWidth="680px"
      >
        <div className="lpz-modal-body">
          <div className="lpz-modal-header">
            <div className="lpz-modal-header-icon" style={{ background: "#eff6ff", color: "#2563eb", borderColor: "#bfdbfe" }}>
              <i className="fa-solid fa-chart-pie"></i>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}>
                CEFR Placement Diagnostic Report
              </h3>
              <p style={{ margin: "0.2rem 0 0 0", fontSize: "0.82rem", color: "#64748b" }}>
                Candidate: <strong>{diagnosticData?.student?.firstName} {diagnosticData?.student?.lastName}</strong> | Language: <strong>{diagnosticData?.test?.language || "Language"}</strong>
              </p>
            </div>
          </div>

          {loadingDiagnostic || !diagnosticData ? (
            <div style={{ textAlign: "center", padding: "3rem" }}>
              <Loader style={{ fontSize: "2.5rem" }} />
              <p style={{ marginTop: "1rem", color: "#64748b" }}>Aggregating diagnostic scores...</p>
            </div>
          ) : (
            <>
              <div className="lpz-diag-grid">
                <div className="lpz-diag-tile">
                  <div className="lpz-diag-tile-label">Written Exam</div>
                  <div className="lpz-diag-tile-val" style={{ color: "#2563eb" }}>
                    {diagnosticData.scores?.written?.obtained} / {diagnosticData.scores?.written?.max}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "4px" }}>
                    {diagnosticData.scores?.written?.percentage}%
                  </div>
                </div>

                <div className="lpz-diag-tile">
                  <div className="lpz-diag-tile-label">Oral Viva</div>
                  <div className="lpz-diag-tile-val" style={{ color: "var(--color-2, #da1c5c)" }}>
                    {diagnosticData.scores?.oral?.obtained} / {diagnosticData.scores?.oral?.max}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "4px" }}>
                    {diagnosticData.scores?.oral?.percentage}%
                  </div>
                </div>

                <div className="lpz-diag-tile" style={{ borderColor: "var(--color-1, #f09a21)" }}>
                  <div className="lpz-diag-tile-label">Composite Score</div>
                  <div className="lpz-diag-tile-val" style={{ color: "var(--color-1, #f09a21)" }}>
                    {diagnosticData.scores?.composite?.totalObtained} / {diagnosticData.scores?.composite?.totalMax}
                  </div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--color-1, #f09a21)", marginTop: "4px" }}>
                    {diagnosticData.scores?.composite?.percentage}%
                  </div>
                </div>
              </div>

              <div className={`lpz-diag-rec-box ${(diagnosticData.scores?.composite?.percentage || 0) < 60 ? "refresher" : ""}`}>
                <div className="lpz-diag-rec-icon">
                  <i className={`fa-solid ${(diagnosticData.scores?.composite?.percentage || 0) >= 60 ? "fa-award" : "fa-triangle-exclamation"}`}></i>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                    CEFR Benchmark Recommendation
                  </div>
                  <div style={{ fontSize: "0.85rem", marginTop: "2px" }}>
                    {diagnosticData.scores?.composite?.systemRecommendation || "Evaluation in progress"}
                  </div>
                </div>
              </div>

              {diagnosticData.scores?.oral?.notes && (
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem" }}>
                  <div style={{ fontSize: "0.76rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", marginBottom: "0.25rem" }}>
                    Teacher Viva Observations:
                  </div>
                  <div style={{ fontSize: "0.84rem", color: "#334155" }}>
                    {diagnosticData.scores.oral.notes}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.75rem", borderTop: "1px solid #e2e8f0" }}>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="lpz-btn lpz-btn-secondary"
                    onClick={() => setIsDiagnosticModalOpen(false)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="lpz-btn lpz-btn-primary"
                    onClick={() => {
                      handleViewResults({
                        _id: diagnosticData.student?._id,
                        placementTest: {
                          testId: diagnosticData.test?._id,
                          answerPaperId: diagnosticData.answerPaperId || diagnosticData.test?.answerPaperId
                        }
                      });
                    }}
                    title="Open full question-wise results and rubric feedback in Results Viewer"
                  >
                    <i className="fa-solid fa-square-poll-vertical"></i> View Results
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "0.5rem 0.9rem", borderRadius: "8px" }}>
                  <i className="fa-solid fa-circle-check" style={{ color: "#16a34a", fontSize: "1rem" }}></i>
                  <span style={{ fontSize: "0.82rem", color: "#166534", fontWeight: 600 }}>
                    Report & Teacher Feedback submitted to Administrator for final CEFR level assignment.
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* MODAL 5: CONFIRM ADMISSION / REASSIGN LEVEL DECISION             */}
      {/* ══════════════════════════════════════════════════════════════════ */}


    </div>
  );
}
